import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, MapPin, RefreshCw } from "lucide-react";
import api from "@/lib/api";

/**
 * IdLocationPicker — cascading picker Provinsi → Kota/Kabupaten →
 * Kecamatan → Kelurahan yang menggunakan data internal Dynicty
 * (`/api/locations/*`). Sepenuhnya independen dari Jubelio, Midtrans, atau
 * shipping provider lain.
 *
 * Props:
 *   value   — { id_province_id, id_regency_id, id_district_id, id_village_id,
 *              id_village_name, province, city, district, subdistrict,
 *              postal_code }
 *   onChange — (patch) => void; menerima patch parsial dan menerima seluruh
 *              nama human-readable + BPS codes.
 *   compact — boolean; layout 1 kolom untuk mobile.
 *
 * Pola API pesan error konsisten: satu banner ringan dengan tombol Coba
 * lagi. Tidak menyebut Jubelio / provider / API.
 */
export default function IdLocationPicker({ value = {}, onChange, compact = false }) {
  const [provinces, setProvinces] = useState([]);
  const [regencies, setRegencies] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState({ p: false, r: false, d: false, v: false });
  const [error, setError] = useState(null);
  const retryTokenRef = useRef(0);

  const fetchList = async (key, url, setter) => {
    setLoading((l) => ({ ...l, [key]: true }));
    setError(null);
    try {
      const { data } = await api.get(url);
      setter(Array.isArray(data) ? data : []);
    } catch (_e) {
      setError("Daftar wilayah belum dapat dimuat. Coba lagi.");
      setter([]);
    } finally {
      setLoading((l) => ({ ...l, [key]: false }));
    }
  };

  // Load provinces on mount + on manual retry.
  useEffect(() => {
    fetchList("p", `/locations/provinces`, setProvinces);
  }, [retryTokenRef.current]); // eslint-disable-line

  // Cascade child loads whenever parent id changes.
  useEffect(() => {
    if (value.id_province_id) {
      fetchList("r", `/locations/regencies?province_id=${value.id_province_id}`, setRegencies);
    } else {
      setRegencies([]);
    }
  }, [value.id_province_id]); // eslint-disable-line

  useEffect(() => {
    if (value.id_regency_id) {
      fetchList("d", `/locations/districts?regency_id=${value.id_regency_id}`, setDistricts);
    } else {
      setDistricts([]);
    }
  }, [value.id_regency_id]); // eslint-disable-line

  useEffect(() => {
    if (value.id_district_id) {
      fetchList("v", `/locations/villages?district_id=${value.id_district_id}`, setVillages);
    } else {
      setVillages([]);
    }
  }, [value.id_district_id]); // eslint-disable-line

  const setField = (patch) => onChange?.(patch);

  const onProvince = (pid) => {
    const p = provinces.find((x) => String(x.id) === String(pid));
    setField({
      id_province_id: pid || "",
      province: p?.name || "",
      // reset children
      id_regency_id: "", city: "",
      id_district_id: "", district: "",
      id_village_id: "", id_village_name: "", subdistrict: "",
      // Jubelio-specific ids also reset — mapping tidak lagi valid.
      province_id: "", city_id: "", district_id: "", area_id: "",
    });
  };

  const onRegency = (rid) => {
    const r = regencies.find((x) => String(x.id) === String(rid));
    setField({
      id_regency_id: rid || "",
      city: r?.name || "",
      id_district_id: "", district: "",
      id_village_id: "", id_village_name: "", subdistrict: "",
      city_id: "", district_id: "", area_id: "",
    });
  };

  const onDistrict = (did) => {
    const d = districts.find((x) => String(x.id) === String(did));
    setField({
      id_district_id: did || "",
      district: d?.name || "",
      id_village_id: "", id_village_name: "", subdistrict: "",
      district_id: "", area_id: "",
    });
  };

  const onVillage = (vid) => {
    const v = villages.find((x) => String(x.id) === String(vid));
    setField({
      id_village_id: vid || "",
      id_village_name: v?.name || "",
      subdistrict: v?.name || "",
      // area_id (Jubelio) tidak diketahui — biarkan kosong.
      area_id: "",
    });
  };

  const retry = () => {
    retryTokenRef.current += 1;
    fetchList("p", `/locations/provinces`, setProvinces);
  };

  const RowSelect = ({ label, testid, options, currentId, onChange: oc, placeholderWhenEmpty, disabled, busy }) => (
    <div>
      <label className="text-xs font-bold text-[#5C5C5C] uppercase tracking-wider flex items-center gap-1">
        {label}
        {busy && <Loader2 size={11} className="animate-spin" />}
      </label>
      <select
        value={currentId || ""}
        onChange={(e) => oc(e.target.value)}
        disabled={disabled || busy}
        className="w-full mt-1 border border-[#E5E5E5] rounded-lg p-2 text-sm bg-white disabled:bg-[#FAF9F6] disabled:cursor-not-allowed disabled:text-[#8A8A8A]"
        data-testid={testid}
      >
        <option value="">
          {disabled ? placeholderWhenEmpty : busy ? "Memuat..." : `— Pilih ${label} —`}
        </option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div
      className={compact ? "space-y-3" : "grid grid-cols-1 md:grid-cols-2 gap-3"}
      data-testid="id-location-picker"
    >
      <RowSelect
        label="Provinsi"
        testid="idloc-province-select"
        options={provinces}
        currentId={value.id_province_id}
        onChange={onProvince}
        busy={loading.p}
        placeholderWhenEmpty="Memuat provinsi..."
      />
      <RowSelect
        label="Kota / Kabupaten"
        testid="idloc-regency-select"
        options={regencies}
        currentId={value.id_regency_id}
        onChange={onRegency}
        disabled={!value.id_province_id}
        busy={loading.r}
        placeholderWhenEmpty="Pilih provinsi terlebih dahulu"
      />
      <RowSelect
        label="Kecamatan"
        testid="idloc-district-select"
        options={districts}
        currentId={value.id_district_id}
        onChange={onDistrict}
        disabled={!value.id_regency_id}
        busy={loading.d}
        placeholderWhenEmpty="Pilih kota / kabupaten terlebih dahulu"
      />
      <RowSelect
        label="Kelurahan / Desa"
        testid="idloc-village-select"
        options={villages}
        currentId={value.id_village_id}
        onChange={onVillage}
        disabled={!value.id_district_id}
        busy={loading.v}
        placeholderWhenEmpty="Pilih kecamatan terlebih dahulu"
      />

      {value.id_village_id && (
        <div
          className="md:col-span-2 text-xs text-[#2D6A4F] bg-[#2D6A4F]/5 border border-[#2D6A4F]/20 rounded-lg px-2 py-1.5 flex items-center gap-1.5"
          data-testid="idloc-summary"
        >
          <MapPin size={12} />
          <span>
            Wilayah terpilih: <b>{value.id_village_name || value.subdistrict}</b>
            {value.district ? `, ${value.district}` : ""}
            {value.city ? `, ${value.city}` : ""}
            {value.province ? `, ${value.province}` : ""}
          </span>
        </div>
      )}

      {error && (
        <div
          className="md:col-span-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 flex items-center justify-between gap-1.5"
          data-testid="idloc-error"
        >
          <span className="flex items-center gap-1.5">
            <RefreshCw size={12} /> {error}
          </span>
          <button
            type="button"
            onClick={retry}
            className="font-bold underline"
            data-testid="idloc-retry-btn"
          >
            Coba lagi
          </button>
        </div>
      )}
    </div>
  );
}
